import { PromiseCompletionSource } from "async/PromiseCompletionSource";
import { ICanvasScaler } from "by/gamefactory/canvas/ICanvasScaler";
import { MyWalletTabsPanel } from "../../components/panel/MyWalletTabsPanel";
import { MyWalletResourcePackage } from "../../resources/MyWalletResourcePackage";
import { IDestroyOptions, Rectangle, Texture } from "pixi.js";
import { MyWalletTab } from "../../components/tabs/MyWalletTab";
import { PiggyClubTab } from "../../components/tabs/PiggyClubTab";
import { BuildingsTab } from "../../components/tabs/BuildingsTab";
import { DiceRollsTab } from "../../components/tabs/DiceRollsTab";
import { TokensTab } from "../../components/tabs/tokens/TokensTab";
import { WalletTabType } from "../../components/WalletTabType";
import { NFTItemsFactory } from "../../components/myWalletNFTs/NFTItemsFactory";
import { CommonResourcesPackage } from "by/gamefactory/cityversetycoon/scenes/common/resources/CommonResourcesPackage";
import { ILogger } from "by/gamefactory/cityversetycoon/logger/ILogger";
import { ButtonWithScaleAnimation } from "by/gamefactory/cityversetycoon/scenes/common/components/ButtonWithScaleAnimation";
import { MyWalletScrollController } from "../../components/myWalletNFTs/MyWalletScrollController";
import { Web3Model } from "by/gamefactory/web3/models/Web3Model";
import { IMyWalletController } from "../../controllers/IMyWalletController";
import { FullscreenInputBlocker } from "by/gamefactory/cityversetycoon/scenes/game/components/FullscreenInputBlocker";
import { MyWalletPage } from "./MyWalletPage";
import { Event } from "event-system";
import { NFTBuilding } from "by/gamefactory/web3/nfts/NFTBuilding";
import { NFTScrollItem } from "../../components/myWalletNFTs/scrollItems/NFTScrollItem";
import { NFTBuildingScrollItem } from "../../components/myWalletNFTs/scrollItems/NFTBuildingScrollItem";
import { DynamicAssets } from "../../../../../../resources/DynamicAssets";
import { IInventory } from "../../../../../../web3/models/IInventory";
import { AsyncOperationNotification } from "../../../../common/components/AsyncOperationNotification";
import { IPlayerModel } from "by/gamefactory/cityversetycoon/scenes/common/models/IPlayerModel";
import { Web3Config } from "../../../../../../web3/config/Web3Config";
import { GameInfo } from "../../../../../../analytics/GameInfo";

export class ScrollPage extends MyWalletPage {
    public readonly onBuildingClick: Event<NFTBuilding>;

    private readonly tabs: Map<WalletTabType, MyWalletTab>;

    private readonly pageCompletionSource: PromiseCompletionSource<void>;
    private readonly myWalletResources: MyWalletResourcePackage;
    private readonly commonResources: CommonResourcesPackage;
    private readonly nftItemsFactory: NFTItemsFactory;
    private readonly gameInfo: GameInfo;

    private tabsPanel: MyWalletTabsPanel;
    private currentTab: MyWalletTab;
    private currentTabType: WalletTabType;
    private leftArrow: ButtonWithScaleAnimation;
    private rightArrow: ButtonWithScaleAnimation;
    private scrollController: MyWalletScrollController;
    private initialized: boolean;

    public constructor(
        canvasScaler: ICanvasScaler,
        pageCompletionSource: PromiseCompletionSource<void>,
        myWalletResources: MyWalletResourcePackage,
        commonResources: CommonResourcesPackage,
        buildingResources: DynamicAssets,
        piggyClubResources: DynamicAssets,
        logger: ILogger,
        inventory: IInventory,
        web3Model: Web3Model,
        web3Config: Web3Config,
        myWalletController: IMyWalletController,
        forceTab: WalletTabType,
        asyncOperationNotification: AsyncOperationNotification,
        playerModel: IPlayerModel,
        gameInfo: GameInfo
    ) {
        super(canvasScaler);
        this.pageCompletionSource = pageCompletionSource;
        this.myWalletResources = myWalletResources;
        this.commonResources = commonResources;
        this.onBuildingClick = new Event();
        this.initialized = true;
        this.nftItemsFactory = new NFTItemsFactory(commonResources, myWalletResources, buildingResources, piggyClubResources, logger, playerModel);
        this.tabs = new Map<WalletTabType, MyWalletTab>([
            [WalletTabType.PIGGY_CLUB, new PiggyClubTab(myWalletResources, this.nftItemsFactory, inventory, piggyClubResources, asyncOperationNotification)],
            [WalletTabType.BUILDING, new BuildingsTab(myWalletResources, buildingResources, this.nftItemsFactory, inventory, asyncOperationNotification, playerModel)],
            [WalletTabType.DICE_ROLLS, new DiceRollsTab(myWalletResources, this.nftItemsFactory, inventory, web3Model, myWalletController, canvasScaler)],
            [WalletTabType.TOKENS, new TokensTab(commonResources, myWalletResources, web3Model, web3Config, myWalletController, canvasScaler, gameInfo, logger)]
        ]);
        this.currentTabType = forceTab;
        this.currentTab = this.tabs.get(this.currentTabType);
        this.currentTab.position.set(164, 145);
        this.addChild(this.currentTab);
        this.build();
        this.scrollController.processArrowsVisible();
        this.tabsPanel.onSwitchTab.add(this.onSwitchTabHandle, this);
        this.tabs.forEach((tab) => {
            tab.isHadNFT.add(this.onTabIsHadNFTHandle, this);
            tab.onItemClick.add(this.onTabItemClickHandle, this);
            tab.updatePointer();
        });
        this.initialized = false;
    }

    public override async show(duration?: number): Promise<void> {
        await Promise.all([this.currentTab.show(), super.show(duration)]);
        if (this.currentTab instanceof BuildingsTab) {
            this.currentTab.updateBuildings();
        }
        if (this.currentTab instanceof PiggyClubTab) {
            this.currentTab.updatePiggyClubs();
        }
    }

    public override async hide(duration?: number): Promise<void> {
        await Promise.all([this.currentTab.hide(), super.hide(duration)]);
    }

    protected override build(): void {
        this.tabsPanel = new MyWalletTabsPanel(this.myWalletResources, this.commonResources, this.currentTabType);
        this.leftArrow = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.leftArrow.hitArea = new Rectangle(-100, - 50, 200, 150);
        this.rightArrow = new ButtonWithScaleAnimation(this.commonResources.assets_common_ui_back_png);
        this.rightArrow.hitArea = new Rectangle(-100, - 50, 200, 150);
        this.rightArrow.scale.x = -1;
        this.scrollController = new MyWalletScrollController(this.leftArrow, this.rightArrow, this.currentTab);

        this.addChild(
            this.tabsPanel,
            this.leftArrow,
            this.rightArrow
        );
        super.build();
    }

    private async onSwitchTabHandle(tabType: WalletTabType): Promise<void> {
        if (this.currentTabType === tabType) {
            return;
        }
        this.switchTab(tabType);
    }

    private async switchTab(tabType: WalletTabType): Promise<void> {
        if (this.currentTabType === tabType) {
            return;
        }
        const inputBlocker = new FullscreenInputBlocker(this.canvasScaler);
        inputBlocker.position.set(0, -this.height);
        this.addChild(inputBlocker);
        await this.currentTab.hide();
        this.removeChild(this.currentTab);
        this.currentTabType = tabType;
        this.currentTab = this.tabs.get(tabType);
        this.scrollController.currentTab = this.currentTab;
        this.currentTab.position.set(164, 145);
        this.addChild(this.currentTab);
        if (this.currentTab instanceof BuildingsTab) {
            this.currentTab.updateBuildings();
        }
        if (this.currentTab instanceof PiggyClubTab) {
            this.currentTab.updatePiggyClubs();
        }
        await this.currentTab.show();
        this.removeChild(inputBlocker);
    }

    private onTabIsHadNFTHandle(tab: WalletTabType, isHadNFT: boolean): void {
        this.tabsPanel.setPointerToPanelElement(tab, isHadNFT);
        if (tab === WalletTabType.DICE_ROLLS && !isHadNFT && !this.initialized) {
            this.pageCompletionSource.complete();
        }
    }

    private onTabItemClickHandle(tabType: WalletTabType, item: NFTScrollItem): void {
        if (tabType === WalletTabType.BUILDING) {
            const building = (item as NFTBuildingScrollItem).data as NFTBuilding;
            this.onBuildingClick.emit(building);
        }
    }

    protected override layout(): void {
        this.tabsPanel.position.set(76, 0);
        this.leftArrow.position.set(132, 848);
        this.rightArrow.position.set(695, 848);
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.tabsPanel.onSwitchTab.remove(this.onSwitchTabHandle, this);
        this.tabs.forEach((tab) => {
            tab.isHadNFT.remove(this.onTabIsHadNFTHandle, this);
            tab.onItemClick.remove(this.onTabItemClickHandle, this);
            tab.destroy(options);
        });
        this.scrollController.destroy();
        super.destroy(options);
    }
}
